import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Globe, ArrowRight, Phone } from "lucide-react";
import { LANGS, LANG_LABELS, useI18n } from "@/i18n";
import { keyFromSlug, routeSlug } from "@/i18n/routes";
import { useAuth, STAFF_ROLES } from "@/context/AuthContext";
import AskVytali, { openAskVytali } from "@/components/AskVytali";
import MobileQuickActions from "@/components/MobileQuickActions";
import { Button } from "@/components/ui/button";

const PUBLIC_LINKS = [
  ["home", "nav.home"],
  ["about", "nav.about"],
  ["opportunities", "nav.opportunities"],
  ["realEstate", "nav.realEstate"],
  ["careers", "nav.careers"],
  ["humanDev", "nav.humanDev"],
  ["projects", "nav.projects"],
  ["contact", "nav.contact"],
];

export function LanguageSwitcher({ testId = "language-switcher" }) {
  const { lang } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();

  const translatePath = (rest, next) => {
    // Public pages use localized slugs: keep the reader on the equivalent page in the new language.
    const key = keyFromSlug(rest);
    if (key) return routeSlug(key, next);
    return rest;
  };

  const switchTo = (next) => {
    window.localStorage.setItem("cs_lang", next);
    const rest = location.pathname.replace(/^\/(fr|en|es)/, "");
    navigate(`/${next}${translatePath(rest, next)}${location.search}`);
  };

  return (
    <div data-testid={testId} className="flex items-center gap-1">
      <Globe className="mr-1 h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
      {LANGS.map((code) => (
        <button
          key={code}
          type="button"
          data-testid={`lang-${code}`}
          onClick={() => switchTo(code)}
          aria-label={LANG_LABELS[code]}
          aria-current={code === lang ? "true" : undefined}
          className={`px-2 py-1 text-xs uppercase tracking-widest transition-colors duration-200 ${
            code === lang ? "text-accent" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {code}
        </button>
      ))}
    </div>
  );
}

export default function PublicLayout({ children }) {
  const { t, lang } = useI18n();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const p = (path) => `/${lang}${path}`;
  const isStaff = user && STAFF_ROLES.includes(user.role);

  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:bg-surface focus:px-4 focus:py-2">
        {t("nav.home")}
      </a>
      <header className="cs-glass fixed inset-x-0 top-0 z-50">
        <div className="cs-container flex h-16 items-center justify-between gap-4 lg:h-20">
          <Link to={p("")} data-testid="brand-link" className="flex shrink-0 flex-col leading-tight">
            <span className="whitespace-nowrap font-display text-base font-semibold tracking-tight sm:text-lg">
              CONSULTANTS SPARKS
            </span>
            <span className="hidden whitespace-nowrap text-[9px] uppercase tracking-[0.18em] text-muted-foreground sm:block">
              {t("footer.tagline")}
            </span>
          </Link>

          <nav aria-label="Primary" className="hidden shrink-0 items-center gap-3 2xl:flex">
            {PUBLIC_LINKS.map(([key, label]) => (
              <NavLink
                key={key}
                to={p(routeSlug(key, lang))}
                end={key === "home"}
                data-testid={`nav-${label.split(".")[1]}`}
                className={({ isActive }) =>
                  `cs-link-underline shrink-0 whitespace-nowrap text-[12.5px] transition-colors duration-200 ${
                    isActive ? "text-accent" : "text-foreground/80 hover:text-foreground"
                  }`
                }
              >
                {t(label)}
              </NavLink>
            ))}
          </nav>

          <div className="hidden shrink-0 items-center gap-2 lg:flex">
            <LanguageSwitcher />
            {user ? (
              <>
                <Link
                  data-testid="nav-account"
                  to={isStaff ? p("/admin") : p("/portal")}
                  className="text-sm text-foreground/80 hover:text-accent"
                >
                  {isStaff ? t("cta.admin") : t("cta.portal")}
                </Link>
                <button data-testid="nav-logout" onClick={logout} className="text-sm text-muted-foreground hover:text-foreground">
                  {t("cta.logout")}
                </button>
              </>
            ) : (
              <Link data-testid="nav-login" to={p("/login")} className="whitespace-nowrap text-[13px] text-foreground/80 hover:text-accent">
                {t("cta.login")}
              </Link>
            )}
            <Button asChild data-testid="nav-apply-cta" className="whitespace-nowrap rounded-full bg-accent px-4 text-[12.5px] text-accent-foreground hover:bg-accent/90">
              <Link to={p(routeSlug("apply", lang))}>{t("cta.apply")}</Link>
            </Button>
          </div>

          <button
            type="button"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? t("common.close") : t("common.menu")}
            className="2xl:hidden"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {open && (
          <div data-testid="mobile-menu" className="border-t border-border bg-surface 2xl:hidden">
            <nav className="cs-container flex flex-col py-4">
              {PUBLIC_LINKS.map(([key, label]) => (
                <Link
                  key={key}
                  to={p(routeSlug(key, lang))}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${label.split(".")[1]}`}
                  className="border-b border-border/60 py-3 text-sm"
                >
                  {t(label)}
                </Link>
              ))}
              <div className="flex flex-wrap items-center justify-between gap-4 py-4">
                <LanguageSwitcher testId="mobile-language-switcher" />
                {user ? (
                  <Link to={isStaff ? p("/admin") : p("/portal")} onClick={() => setOpen(false)} className="text-sm">
                    {isStaff ? t("cta.admin") : t("cta.portal")}
                  </Link>
                ) : (
                  <Link to={p("/login")} onClick={() => setOpen(false)} data-testid="mobile-nav-login" className="text-sm">
                    {t("cta.login")}
                  </Link>
                )}
              </div>
              <Button asChild data-testid="mobile-apply-cta" className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
                <Link to={p(routeSlug("apply", lang))} onClick={() => setOpen(false)}>
                  {t("cta.apply")}
                </Link>
              </Button>
            </nav>
          </div>
        )}
      </header>

      <main id="main" className="flex-1 pt-16 lg:pt-20">
        {children}
      </main>

      <footer className="border-t border-border bg-primary text-primary-foreground">
        <div className="cs-container grid grid-cols-1 gap-10 py-16 md:grid-cols-12">
          <div className="md:col-span-5">
            <p className="font-display text-2xl">CONSULTANTS SPARKS</p>
            <p className="mt-2 text-xs uppercase tracking-[0.22em] text-primary-foreground/60">{t("footer.tagline")}</p>
            <Button asChild variant="outline" className="mt-8 rounded-full border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground hover:text-primary focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2">
              <Link to={p(routeSlug("apply", lang))} data-testid="footer-apply-cta">
                {t("cta.apply")} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
          <div className="md:col-span-3">
            <p className="cs-kicker text-primary-foreground/50">{t("footer.navTitle")}</p>
            <ul className="mt-4 space-y-2 text-sm">
              {PUBLIC_LINKS.map(([key, label]) => (
                <li key={key}>
                  <Link to={p(routeSlug(key, lang))} className="text-primary-foreground/80 hover:text-accent">
                    {t(label)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-4">
            <p className="cs-kicker text-primary-foreground/50">{t("footer.contactTitle")}</p>
            <a
              data-testid="footer-phone-link"
              href="tel:+14185496833"
              aria-label={`${t("footer.callUs")} +1 418-549-6833`}
              className="mt-4 inline-flex items-center gap-2 text-base text-primary-foreground hover:text-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent"
            >
              <Phone className="h-4 w-4" aria-hidden="true" />
              +1 418-549-6833
            </a>
            <p className="mt-1 text-xs uppercase tracking-[0.18em] text-primary-foreground/50">{t("footer.callUs")}</p>
            <p data-testid="footer-address" className="mt-5 whitespace-pre-line text-xs leading-relaxed text-primary-foreground/70">
              {t("footer.address")}
            </p>
            <ul className="mt-5 space-y-2 text-sm">
              <li>
                <Link to={p(routeSlug("contact", lang))} className="text-primary-foreground/80 hover:text-accent">
                  {t("nav.contact")}
                </Link>
              </li>
              <li>
                <Link to={p("/login")} className="text-primary-foreground/80 hover:text-accent">
                  {t("cta.login")}
                </Link>
              </li>
              <li>
                <Link data-testid="footer-privacy-link" to={p(routeSlug("privacy", lang))} className="text-primary-foreground/80 hover:text-accent">
                  {t("footer.privacy")}
                </Link>
              </li>
              <li>
                <Link data-testid="footer-terms-link" to={p(routeSlug("terms", lang))} className="text-primary-foreground/80 hover:text-accent">
                  {t("footer.terms")}
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="cs-container border-t border-primary-foreground/10 py-10">
          <div data-testid="footer-ask-vytali" className="max-w-2xl">
            <p className="cs-kicker text-primary-foreground/50">{t("footer.askTitle")}</p>
            <p className="mt-3 text-sm leading-relaxed text-primary-foreground/80">{t("footer.askLead")}</p>
            <Button variant="outline" data-testid="footer-ask-cta" onClick={openAskVytali} className="mt-5 rounded-full border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground hover:text-primary focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2">
              {t("footer.askCta")} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <p className="mt-3 text-xs text-primary-foreground/40">{t("footer.askSoon")}</p>
          </div>
        </div>
        <div className="cs-container border-t border-primary-foreground/10 py-8 pb-32 sm:pb-8">
          <p data-testid="footer-confidentiality" className="max-w-3xl text-xs leading-relaxed text-primary-foreground/60">
            {t("footer.confidentiality")}
          </p>
          <p className="mt-3 text-xs text-primary-foreground/40">
            © {new Date().getFullYear()} Consultants Sparks. {t("footer.rights")}
          </p>
        </div>
      </footer>

      <AskVytali />
      <MobileQuickActions />
    </div>
  );
}
